import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import cx from 'classnames';
import _debounce from 'lodash/debounce';

import { COLORS3 } from '../../styles/emotion';
import { OverflowEllipsis, OverflowEllipsisProps } from '../OverflowEllipsis/OverflowEllipsis';

import styles from './viewMore.module.less';
import LinkV2 from '../LinkV2/LinkV2';

export type ViewMoreProps = OverflowEllipsisProps & {
  backgroundColor?: string;
  /**
   * Optional override for the default view more link to expand the hidden content.  Can be used
   * to provide a link with different font size or CTA, or do to something completely different
   * like navigate to a different page.
   */
  viewMore?: (props: { isOpen: boolean; setIsOpen: (isOpen: boolean) => void }) => JSX.Element;

  /**
   * Optional override for the default view less link to collapse the content.  Can be used to
   * suppress the view less action by providing a function that returns an empty fragment.
   */
  viewLess?: (props: { isOpen: boolean; setIsOpen: (isOpen: boolean) => void }) => JSX.Element;
};

/**
 * Provides up to N lines of text, and if there is more text, includes a View More link
 * to expand the selection to as large as it needs to be
 */
export const ViewMore: React.FC<ViewMoreProps> = (props) => {
  const {
    backgroundColor = COLORS3.WHITE_100,
    className,
    children,
    viewMore,
    viewLess,
    ...rest
  } = props;

  const viewMoreRef = useRef<HTMLDivElement>(null);
  const [showControls, setShowControls] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const fadeStyle = useMemo(
    () => ({
      backgroundImage: `linear-gradient(to right, transparent, ${backgroundColor} 80%)`,
    }),
    [backgroundColor]
  );

  const determineShowMore = useCallback(() => {
    const { current } = viewMoreRef;
    if (current) {
      setShowControls(current.scrollHeight !== current.clientHeight);
    }
  }, [viewMoreRef]);

  const debounceFn = useMemo(() => _debounce(determineShowMore, 500), [determineShowMore]);
  useEffect(
    () => {
      window.addEventListener('resize', debounceFn);
      return () => {
        window.removeEventListener('resize', debounceFn);
      };
    },
    // Because we memoized the debounce func, it will only invoke useEffect once the timeout completes.
    [debounceFn]
  );

  useEffect(() => {
    determineShowMore();
  }, [determineShowMore]);

  // Make sure the component is visible before reading scroll & client heights
  useEffect(() => {
    const { current } = viewMoreRef;

    if (current) {
      const observer = new IntersectionObserver((entries) => {
        const intersectingEntry = entries.find((entry) => entry.isIntersecting);

        if (intersectingEntry) {
          determineShowMore();
          observer.unobserve(current);
        }
      });

      observer.observe(current);
    }
  }, [viewMoreRef, determineShowMore]);

  const viewMoreElement = useMemo(() => {
    if (viewMore) {
      return viewMore({ isOpen, setIsOpen });
    }
    return (
      <LinkV2 className={styles.button} role="button" type="button" onClick={() => setIsOpen(true)}>
        View more
      </LinkV2>
    );
  }, [isOpen, viewMore, setIsOpen]);

  const viewLessElement = useMemo(() => {
    if (viewLess) {
      return viewLess({ isOpen, setIsOpen });
    }
    return (
      <LinkV2
        className={styles.button}
        role="button"
        type="button"
        onClick={() => setIsOpen(false)}
      >
        View less
      </LinkV2>
    );
  }, [isOpen, viewLess]);

  return (
    <OverflowEllipsis
      className={cx(className, styles.viewMore, { [styles.open]: isOpen })}
      ref={viewMoreRef}
      style={{ backgroundColor }}
      {...rest}
    >
      {children}
      {showControls && (
        <Fragment>
          <div className={styles.more}>
            <div className={styles.fade} style={fadeStyle} />
            {viewMoreElement}
          </div>
          <div className={styles.less}>
            &ensp;
            {viewLessElement}
          </div>
        </Fragment>
      )}
    </OverflowEllipsis>
  );
};
